import LoadMoreCard from "@/components/Card/LoadMoreCard";
import ContentHeader from "@/components/ContentHeader";
import Loading from "@/components/Loading";
import SliderCore from "@/components/SliderCore";
import Card from "@/components/Card/index";
import {
  useDeviceInfoQuery,
  useCopyrights,
  useIntersectionObserver,
} from "@/hooks";
import { SLIDER_MAX_NUM } from "@/lib/constants";
import styles from "@/styles/Slider.module.scss";
import classnames from "classnames";
import {
  cloneElement,
  createElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";

// 複数行にする場合はslidesPerColumnを指定する
const defaultSliderOptions = {
  slidesPerView: 2,
  slidesPerGroup: 2,
  breakpoints: {
    500: {
      slidesPerView: 3,
      slidesPerGroup: 3,
    },
    800: {
      slidesPerView: 4,
      slidesPerGroup: 4,
    },
    1100: {
      slidesPerView: 5,
      slidesPerGroup: 5,
    },
    1400: {
      slidesPerView: 6,
      slidesPerGroup: 6,
    },
  },
  navigation: true,
  // pagination: { clickable: true },
  // lazy: true,
  freeMode: false,
};

const largeCardBreakpoints = {
  breakpoints: {
    500: {
      slidesPerView: 2,
      slidesPerGroup: 2,
    },
    800: {
      slidesPerView: 3,
      slidesPerGroup: 3,
    },
    1100: {
      slidesPerView: 4,
      slidesPerGroup: 4,
    },
  },
};

export default function Slider({
  header,
  itemComponent = Card,
  loading,
  error,
  items,
  largeCard = false,
  noContent,
  className,
  options = {},
}) {
  if (error) {
    // TODO: error
    console.log(error);
    return null;
  }

  const rank = items?.data[0]?.rank;
  const sliderRef = useRef();
  const intersect = useIntersectionObserver(sliderRef, {
    root: null,
    rootMargin: "100px",
    threshold: 0.0,
  });
  const { setCopyrights } = useCopyrights();
  const { data } = useDeviceInfoQuery({ fetchPolicy: "cache-first" });
  const deviceInfo = data?.deviceInfo;

  const sliderOptions = useMemo(() => {
    if (deviceInfo && deviceInfo?.isMobile) {
      defaultSliderOptions.freeMode = true;
    }
    if (largeCard) {
      return Object.assign(
        {},
        defaultSliderOptions,
        largeCardBreakpoints,
        options
      );
    } else {
      return Object.assign({}, defaultSliderOptions, options);
    }
  }, [largeCard, deviceInfo, options]);

  const [focusIndex, setFocusIndex] = useState(null);
  const [slidesPerView, setSlidesPreView] = useState();
  const [activeIndex, setActiveIndex] = useState();
  const [swiperControl, setSwiperControl] = useState();

  const onSwiper = useCallback((control) => {
    setSwiperControl(control);
  }, []);

  const handleMouseEnter = useCallback(
    (e) => {
      if (swiperControl && !swiperControl?.destroyed && !largeCard) {
        const { slides } = swiperControl;
        const current = e.currentTarget.parentElement;
        const currentIndex = slides.indexOf(current);
        setFocusIndex(currentIndex);
      }
    },
    [swiperControl, largeCard]
  );

  const handleMouseLeave = useCallback(() => {
    setFocusIndex(null);
  }, [swiperControl]);

  useEffect(() => {
    const breakPointHandler = () => {
      if (swiperControl && !swiperControl?.destroyed) {
        const slidesPerView = swiperControl.params.slidesPerView;
        setSlidesPreView(slidesPerView);
      }
    };
    const activeIndexHandler = () => {
      if (swiperControl && !swiperControl?.destroyed) {
        const activeIndex = swiperControl.activeIndex;
        setActiveIndex(activeIndex);
      }
    };
    if (swiperControl) {
      swiperControl.on("breakpoint", breakPointHandler);
      breakPointHandler();
      swiperControl.on("activeIndexChange", activeIndexHandler);
      activeIndexHandler();
    }
    return () => {
      if (swiperControl) {
        swiperControl.off("breakpoint", breakPointHandler);
        swiperControl.off("activeIndexChange", activeIndexHandler);
      }
    };
  }, [swiperControl]);

  useEffect(() => {
    if (items) {
      const copyrights = items.data.map((item) => item?.copyright);
      setCopyrights(copyrights);
    }
  }, [items]);

  const children = useMemo(() => {
    if (!loading && items) {
      const children = items.data.map((item, i) => (
        <div
          key={`item-${i}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {createElement(itemComponent, {
            item,
            index: i,
            focusIndex,
            slidesPerView,
            activeIndex,
            slider: true,
            intersect,
          })}
        </div>
      ));

      if (items.pageInfo.hasNextPage && header?.link) {
        children.push(
          <div
            key={`item-${SLIDER_MAX_NUM}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <LoadMoreCard
              {...{
                index: SLIDER_MAX_NUM,
                focusIndex,
                slidesPerView,
                activeIndex,
                link: header?.link,
              }}
            />
          </div>
        );
      }
      return children;
    } else {
      return null;
    }
  }, [loading, items, focusIndex, activeIndex, slidesPerView, intersect]);

  let noContentEle;
  if (!loading && items?.data?.length === 0) {
    if (noContent) {
      noContentEle = cloneElement(noContent);
      // return <div className={styles.slider}>{cloneElement(noContent)}</div>;
    } else {
      return null;
    }
  }

  if (!deviceInfo) return null;

  return (
    <div
      className={classnames(styles.slider, className, {
        [styles.feature]: focusIndex != null,
        [styles.largeCard]: largeCard,
        [styles.mobile]: deviceInfo?.isMobile,
        [styles.rankCard]: rank,
      })}
      ref={sliderRef}
    >
      {header && <ContentHeader {...header} />}
      {loading && <Loading />}
      {!loading && items?.data?.length === 0 && <>{noContentEle}</>}
      {!loading && items && (
        <SliderCore sliderOptions={sliderOptions} onSwiper={onSwiper}>
          {children}
        </SliderCore>
      )}
    </div>
  );
}
